import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              <span className="font-semibold">Cookies</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none">
          <h1 className="text-3xl font-bold mb-2">Cookie Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: January 7, 2024</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">1. What Are Cookies?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how their site is being used.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device when you go offline, while session cookies are deleted as soon as you close your web browser.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">2. How We Use Cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              YASNO AI uses cookies and similar tracking technologies for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Essential Operations:</strong> To enable core functionality such as security, network management, and account access</li>
              <li><strong>Performance & Analytics:</strong> To understand how visitors interact with our website and improve our services</li>
              <li><strong>Personalization:</strong> To remember your preferences and settings</li>
              <li><strong>Authentication:</strong> To keep you logged in and secure your session</li>
              <li><strong>Marketing:</strong> To deliver relevant advertisements and measure campaign effectiveness (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">3. Types of Cookies We Use</h2>
            
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-medium mb-3 text-primary">🔒 Strictly Necessary Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                These cookies are essential for the website to function and cannot be switched off. They are usually set in response to actions you take, such as logging in or filling out forms.
              </p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-muted-foreground">Cookie Name</th>
                      <th className="text-left py-2 text-muted-foreground">Purpose</th>
                      <th className="text-left py-2 text-muted-foreground">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-white/5">
                      <td className="py-2">session_token</td>
                      <td className="py-2">Authentication & session management</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">csrf_token</td>
                      <td className="py-2">Security - prevents cross-site request forgery</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-medium mb-3 text-blue-400">📊 Analytics Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-muted-foreground">Cookie Name</th>
                      <th className="text-left py-2 text-muted-foreground">Provider</th>
                      <th className="text-left py-2 text-muted-foreground">Purpose</th>
                      <th className="text-left py-2 text-muted-foreground">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-white/5">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics</td>
                      <td className="py-2">Distinguishes unique users</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">_ga_*</td>
                      <td className="py-2">Google Analytics 4</td>
                      <td className="py-2">Stores session state</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics</td>
                      <td className="py-2">Distinguishes users</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-medium mb-3 text-purple-400">⚙️ Functional Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences.
              </p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-muted-foreground">Cookie Name</th>
                      <th className="text-left py-2 text-muted-foreground">Purpose</th>
                      <th className="text-left py-2 text-muted-foreground">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-white/5">
                      <td className="py-2">language</td>
                      <td className="py-2">Remembers your language preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">theme</td>
                      <td className="py-2">Remembers dark/light mode preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">timezone</td>
                      <td className="py-2">Stores your timezone for scheduling</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-3 text-orange-400">📢 Marketing Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                These cookies are used to track visitors across websites to display relevant advertisements. They are set only with your explicit consent.
              </p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-muted-foreground">Cookie Name</th>
                      <th className="text-left py-2 text-muted-foreground">Provider</th>
                      <th className="text-left py-2 text-muted-foreground">Purpose</th>
                      <th className="text-left py-2 text-muted-foreground">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-white/5">
                      <td className="py-2">_fbp</td>
                      <td className="py-2">Facebook</td>
                      <td className="py-2">Tracks visits across websites for ads</td>
                      <td className="py-2">3 months</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">_gcl_au</td>
                      <td className="py-2">Google Ads</td>
                      <td className="py-2">Conversion tracking</td>
                      <td className="py-2">3 months</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">li_sugr</td>
                      <td className="py-2">LinkedIn</td>
                      <td className="py-2">Browser identifier for ads</td>
                      <td className="py-2">3 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">4. Third-Party Cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. The third parties include:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Google Analytics:</strong> For website analytics and performance measurement</li>
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Intercom/Crisp:</strong> For customer support chat functionality</li>
              <li><strong>Social Media Platforms:</strong> For social sharing features and embedded content</li>
              <li><strong>Advertising Partners:</strong> For delivering relevant advertisements (with consent)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Please refer to these third parties' respective privacy policies for more information about their cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">5. Managing Your Cookie Preferences</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">5.1 Cookie Consent Banner</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you first visit our website, you will see a cookie consent banner that allows you to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Accept all cookies</li>
              <li>Reject non-essential cookies</li>
              <li>Customize your preferences by cookie category</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 text-foreground">5.2 Browser Settings</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>View cookies stored on your device</li>
              <li>Delete all or specific cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all third-party cookies</li>
              <li>Clear all cookies when you close your browser</li>
            </ul>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h4 className="font-medium mb-2 text-foreground">Browser-Specific Instructions:</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Privacy, Search & Services → Cookies</li>
              </ul>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mt-4">
              <p className="text-muted-foreground text-sm">
                <strong>⚠️ Note:</strong> Blocking or deleting cookies may impact your experience on our website. Some features may not function properly without certain cookies.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">6. Do Not Track (DNT) Signals</h2>
            <p className="text-muted-foreground leading-relaxed">
              Some browsers have a "Do Not Track" (DNT) feature that sends a signal to websites requesting that your browsing behavior not be tracked. Our website currently does not respond to DNT signals. However, you can manage your tracking preferences using the cookie consent banner or your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">7. Updates to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">8. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">If you have questions about our use of cookies, please contact us:</p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-2">
              <p className="text-muted-foreground">YASNO Marketing LLC</p>
              <p className="text-muted-foreground">Email: support@yasnomktg.com</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">9. Related Policies</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">For more information about how we handle your data, please see:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-primary hover:underline">Terms of Service</Link></li>
              <li><Link to="/gdpr" className="text-primary hover:underline">GDPR Rights</Link></li>
            </ul>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Button
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="rounded-full"
          >
            Back to Top
          </Button>
        </div>
      </main>
    </div>
  );
}
